// DynamoDB Entity Type Interfaces
// Based on original toolbox.js entity definitions

export interface BaseEntity {
  pk: string;
  sk: string;
  id: string;
  created_at?: string;
  modified?: string;
  entity?: string;
}

export interface ArchivableEntity extends BaseEntity {
  archived_at?: string | null;
}

export interface User extends ArchivableEntity {
  name?: string;
  email: string;
  first_name: string;
  middle_initial?: string;
  last_name: string;
  type: 'admin' | 'director' | 'instructor' | 'student';
  course_ids?: string[];
  student_group_ids?: string[];
  student_id?: string;
}

export interface Course extends ArchivableEntity {
  name: string;
  assignment_ids?: string[];
  instructor_ids?: string[];
  assessments?: CourseAssessment[];
  gsi1pk?: string;
  gsi1sk?: string;
}

export interface CourseAssessment {
  assessment_id: string;
  day_index?: number;
}

export interface Survey extends ArchivableEntity {
  name: string;
  course_id?: string;
  fields?: SurveyField[];
  gsi1pk?: string;
  gsi1sk?: string;
}

export interface SurveyField {
  name: string;
  type: 'text' | 'number' | 'date';
}

export interface StudentGroup extends ArchivableEntity {
  name: string;
  max_cohort_size?: number;
  student_ids?: string[];
  assignment_groupings?: AssignmentGrouping[];
  course_id?: string;
  gsi1pk?: string;
  gsi1sk?: string;
}

export interface AssignmentGrouping {
  assignment_ids: string[];
}

export interface Student extends ArchivableEntity {
  first_name: string;
  last_name: string;
  middle_initial?: string;
  assignment_id?: string;
  course_id?: string;
  student_group_id?: string;
  attributes?: Record<string, string | number | boolean>;
  gsi1pk?: string;
  gsi1sk?: string;
}

export interface Assessment extends ArchivableEntity {
  name: string;
  type?: string;
  is_official?: boolean;
  student_ratio?: number;
  team_size?: number;
  duration?: number;
  events?: AssessmentEvent[];
  course_id?: string;
  gsi1pk?: string;
  gsi1sk?: string;
}

export interface AssessmentEvent {
  event_id: string;
}

export interface Attempt extends ArchivableEntity {
  scores: number[];
  notes?: string;
  assessment_id?: string;
  event_id?: string;
  student_id?: string;
  attempted_at?: string;
  attempt_index?: number;
  assessment_instance_id?: string;
  measurement_results?: MeasurementResult[];
  team_id?: string;
  team_members?: string[];
  team_attempt_group?: string;
  biometric_context?: BiometricContext;
  gsi1pk?: string;
  gsi1sk?: string;
  gsi2pk?: string;
  gsi2sk?: string;
}

export interface Disqualification extends BaseEntity {
  event_id?: string;
  assessment_id?: string;
  student_id?: string;
  reason?: string;
  notes?: string;
  assessment_instance_id?: string;
  gsi1pk?: string;
  gsi1sk?: string;
  gsi2pk?: string;
  gsi2sk?: string;
}

export interface FireTeam {
  label?: string;
  participants?: string[];
}

export interface Lane {
  instructors?: string[];
  teams?: FireTeam[];
}

export interface Instance extends ArchivableEntity {
  name?: string;
  assessment_id?: string;
  student_group_id?: string;
  cohortLabel?: string;
  date?: string;
  is_official?: boolean;
  started_at?: string;
  venue?: string;
  address?: string;
  started_by?: string;
  lanes?: Lane[];
  gsi1pk?: string;
  gsi1sk?: string;
}

// ---------------------------------------------------------------------------
// Measurement types
// ---------------------------------------------------------------------------

export type MeasurementKind = 'pass_fail' | 'time' | 'distance' | 'quantity';
export type AggregationType = 'best' | 'cumulative' | 'average';

export interface UnitPart {
  label: string;
  factor: number;
  max?: number;
}

export interface UnitScheme {
  base_unit: string;
  parts: UnitPart[];
}

export interface MeasurementDef {
  measurement_id: string;
  name: string;
  kind: MeasurementKind;
  unit_scheme: UnitScheme;
  aggregation: AggregationType;
  lower_is_better?: boolean;
}

export interface MeasurementResult {
  measurement_id: string;
  raw_parts?: number[];
  raw_normalized: number;
  points?: number | null;
  score_table_version?: string;
  score_table_id?: string;
}

// ---------------------------------------------------------------------------
// Scoring table types
// ---------------------------------------------------------------------------

export interface ScoreTableRow {
  sex?: 'M' | 'F' | 'X';
  age_min?: number;
  age_max?: number;
  result_min: number;
  result_max: number;
  points: number;
}

export interface GeneratorParams {
  max_points: number;
  min_points: number;
  top_result: number;
  bottom_result: number;
  sex?: 'M' | 'F' | 'X';
  age_min?: number;
  age_max?: number;
  p25_result?: number;
  p75_result?: number;
  result_count_at_generation?: number;
}

export interface GeneratorProvenance extends GeneratorParams {
  curve: string;
  breakpoints: { p25_points: number; p75_points: number };
  prior_table_id?: string;
  distribution?: {
    count: number; min: number; max: number;
    p1: number; p25: number; p75: number; p99: number;
  };
}

export interface ScoreTable extends ArchivableEntity {
  event_id: string;
  measurement_id: string;
  version: string;
  effective_date?: string;
  label?: string;
  dimensions?: string[];
  rows: ScoreTableRow[];
  status?: 'active' | 'pending_approval' | 'rejected' | 'superseded';
  source?: 'generated' | 'csv' | 'manual';
  generator?: GeneratorProvenance;
  result_count?: number;
  gsi1pk?: string;
  gsi1sk?: string;
}

// ---------------------------------------------------------------------------
// Tally types
// ---------------------------------------------------------------------------

export interface TallyEventEntry {
  event_id: string;
  measurement_id: string;
  points: number;
  score_table_version?: string;
}

export interface Tally extends BaseEntity {
  student_id: string;
  assessment_id: string;
  event_entries: TallyEventEntry[];
  total_points: number;
  computed_at: string;
  gsi1pk?: string;
  gsi1sk?: string;
}

// ---------------------------------------------------------------------------
// Event
// ---------------------------------------------------------------------------

export type TeamSizeExact = { exact: number };
export type TeamSizeRange = { min: number; max: number };
export type TeamSize = TeamSizeExact | TeamSizeRange;

export interface Team extends ArchivableEntity {
  name: string;
  member_ids: string[];
  status: 'active' | 'archived';
  created_at?: string;
}

export interface Event extends ArchivableEntity {
  name: string;
  max_attempts?: number;
  fallback_event_id?: string;
  behaviors?: EventBehavior[];
  grade_entire_team?: boolean;
  // Legacy scalar fields — still present on old records, replaced by measurements[].
  /** @deprecated Use measurements[] and scoring_mode instead. */
  scoring_type?: 'pass_fail' | 'quantity' | 'distance' | 'time' | 'subjective';
  score_aggregation_type?: 'best' | 'cumulative' | 'average';
  /** @deprecated Use measurements[].unit_scheme_id or measurements[].unit_scheme instead. */
  unit?: string;
  // New: N measurements per event.
  measurements?: MeasurementDef[];
  // Explicit mode — inferred from measurements presence on legacy records.
  scoring_mode?: 'pass_fail' | 'measured';
  // Team participation config
  participation?: 'individual' | 'team';
  team_size?: TeamSize;
  team_scoring?: 'single_result' | 'aggregate';
  // Wearable context config
  wearables?: { enabled: boolean; categories?: string[] };
}

export interface EventBehavior {
  name: string;
}

export interface Assignment extends ArchivableEntity {
  name: string;
}

export interface CourseScheduleInstance extends ArchivableEntity {
  course_id?: string;
  start_date?: string;
  venue?: string;
  instances?: string[];
  student_group_id?: string;
}

export interface Ranking extends ArchivableEntity {
  name: string;
}

export interface Taxonomy extends ArchivableEntity {
  taxonomyKey: string;
  label: string;
  description?: string;
}

export interface TaxonomyTerm extends ArchivableEntity {
  taxonomyKey: string;
  code: string;
  label: string;
  parentCode?: string;
}

// Permission types
export type UserRole = 'admin' | 'director' | 'instructor' | 'student';

export interface RoleAssignment {
  role: string;
  orgPath: string;
}

export type Capability =
  | 'view_events'
  | 'manage_events'
  | 'manage_assessments'
  | 'schedule_instances'
  | 'record_attempts'
  | 'manage_scoring_tables'
  | 'approve_scoring_tables'
  | 'manage_users'
  | 'manage_roles'
  | 'manage_org'
  | 'manage_teams'
  | 'view_analytics'
  | 'manage_licensing'
  | 'manage_integrations';

export interface Permissions {
  assignments: RoleAssignment[];
  capabilities: Record<Capability, boolean>;
}

export const EMPTY_PERMISSIONS: Permissions = {
  assignments: [],
  capabilities: {} as Record<Capability, boolean>,
};

/** Returns true if any assignment in the list matches the given role. */
export const hasRole = (
  permissions: Permissions | undefined | null,
  role: string,
): boolean => {
  if (!permissions) return false;
  return permissions.assignments.some((a) => a.role === role);
};

/** Returns true if the user holds the given capability per GET /me. */
export const can = (
  permissions: Permissions | undefined | null,
  capability: Capability,
): boolean => {
  if (!permissions) return false;
  return permissions.capabilities[capability] === true;
};

// Entity name to type mapping
export type EntityName =
  | 'USER'
  | 'COURSE'
  | 'SURVEY'
  | 'STUDENTGROUP'
  | 'STUDENT'
  | 'ASSESSMENT'
  | 'ATTEMPT'
  | 'DISQUALIFICATION'
  | 'INSTANCE'
  | 'EVENT'
  | 'SCORETABLE'
  | 'TALLY'
  | 'ASSIGNMENT'
  | 'COURSESCHEDULEINSTANCE'
  | 'RANKING'
  | 'TAXONOMY'
  | 'TAXONOMYTERM'
  | 'ORGUNIT';

// OrgUnit REST entity
export interface OrgUnit {
  id: string;      // the URL-encoded path (used as react-admin record id)
  path: string;    // e.g. "/aetc/2af"
  name: string;
  echelonType?: string;
  parentPath?: string;
  taxonomyRefs?: string[];
  archived_at?: string | null;
}

// ---------------------------------------------------------------------------
// Wearable / biometric types
// ---------------------------------------------------------------------------

export interface BiometricReadiness {
  date?: string;
  hrv_ms?: number | null;
  resting_hr?: number | null;
  sleep_duration_min?: number | null;
  sleep_score?: number | null;
  readiness_score?: number | null;
  source: string;
}

export interface BiometricEffort {
  avg_hr?: number | null;
  max_hr?: number | null;
  hr_zones?: Record<string, number> | null;
  window_start?: string;
  window_end?: string;
  source: string;
}

export interface BiometricStrainTrend {
  last7_avg?: number | null;
  last28_avg?: number | null;
}

export interface BiometricContext {
  readiness: BiometricReadiness | null;
  effort: BiometricEffort | null;
  strain_trend: BiometricStrainTrend | null;
  attached_at: string;
  consent_status_at_attach: string;
}

export interface WearableConsent {
  student_id: string;
  status: 'granted' | 'revoked' | 'none';
  basis?: string;
  granted_at?: string;
  revoked_at?: string;
  recorded_by?: string;
  history?: unknown[];
}
